// FromTimeEntriesModal.jsx — build an invoice out of a period's logged hours.
//
// The server does the deciding (services/timeEntryInvoicing): indirect time and
// non-billable projects are never invoiced, and hours with no resolvable rate
// come back as `unpriced` rather than being billed at zero. This form's job is
// to collect the period and then SHOW those refusals plainly — an operator who
// cannot see what was left out will assume everything was included.

import React, { useEffect, useState } from "react";
import { listClients } from "../../api/invoices/invoices";
import { post } from "../../api";
import { fieldSetter } from "../../helpers/formEvents";

// Monday of the current week back to the Monday four weeks earlier — a
// sensible default billing period rather than an empty pair of date fields.
function defaultPeriod() {
  const now = new Date();
  const day = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const dow = day.getUTCDay();
  const monday = new Date(day.getTime() - ((dow === 0 ? 6 : dow - 1) * 86400000));
  const from = new Date(monday.getTime() - 28 * 86400000);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from: iso(from), to: iso(monday) };
}

export default function FromTimeEntriesModal({ onClose, onCreated }) {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [{ from, to }, setPeriod] = useState(defaultPeriod);
  const [hourlyRate, setHourlyRate] = useState('');
  const [allowUnpriced, setAllowUnpriced] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [unpriced, setUnpriced] = useState([]);
  const [skipped, setSkipped] = useState(null);

  useEffect(() => {
    (async () => {
      try { const r = await listClients(); setClients((r && r.data) || []); }
      catch (_) { /* the picker just shows empty */ }
    })();
  }, []);

  const submit = async () => {
    setError(''); setUnpriced([]); setSkipped(null);
    if (!clientId) { setError('Pick a client'); return; }
    setBusy(true);
    try {
      const r = await post('/api/invoices/from-time-entries', {
        clientId, from, to,
        invoiceNumber: invoiceNumber.trim(),
        hourlyRate: hourlyRate === '' ? null : Number(hourlyRate),
        allowUnpriced,
      }, false);
      setSkipped((r && r.skipped) || null);
      onCreated(r && r.data, r);
    } catch (e) {
      // The server refuses on purpose for two cases; surface both as guidance
      // rather than a bare failure.
      const payload = (e && e.response) || e || {};
      setError(payload.error || (e && e.message) || 'Could not build the invoice');
      if (payload.unpriced) setUnpriced(payload.unpriced);
      if (payload.skipped) setSkipped(payload.skipped);
    } finally { setBusy(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', width: 560, maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto', borderRadius: 8, padding: 22 }} onClick={(e) => e.stopPropagation()}>
        <div className="d-flex align-items-baseline justify-content-between mb-3">
          <h5 className="mb-0">Invoice from time entries</h5>
          <button className="acc-btn" onClick={onClose}>Close</button>
        </div>

        {error && <div className="alert alert-warning" style={{ fontSize: 13 }}>{error}</div>}

        {unpriced.length > 0 && (
          <div className="alert alert-warning" style={{ fontSize: 13 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>These hours have no hourly rate:</div>
            {unpriced.map((u) => (<div key={u.description}>{u.description} — {u.hours}h</div>))}
            <label style={{ display: 'block', marginTop: 8 }}>
              <input type="checkbox" checked={allowUnpriced} onChange={(e) => setAllowUnpriced(e.target.checked)} />
              {' '}Invoice them anyway, at zero
            </label>
          </div>
        )}

        <label style={{ fontSize: 12, color: '#666' }}>Client *</label>
        <select className="form-control mb-2" value={clientId} onChange={(e) => setClientId(e.target.value)}>
          <option value="">Select a client…</option>
          {clients.map((c) => (<option key={c._id} value={c._id}>{c.name}</option>))}
        </select>

        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 12, color: '#666' }}>From</label>
            <input type="date" className="form-control mb-2" value={from} onChange={fieldSetter(setPeriod, 'from')} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 12, color: '#666' }}>To</label>
            <input type="date" className="form-control mb-2" value={to} onChange={fieldSetter(setPeriod, 'to')} />
          </div>
        </div>
        {/* The window is not only a filter on the hours: it is what the invoice
            says it covers. Nobody re-enters it on the invoice afterwards. */}
        <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>
          This window becomes the invoice's effective dates — the period it
          covers, shown on the document and in both lists.
        </div>

        <label style={{ fontSize: 12, color: '#666' }}>Invoice number</label>
        <input className="form-control mb-2" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="TE-032026" />

        <label style={{ fontSize: 12, color: '#666' }}>Hourly rate override</label>
        <input type="number" min="0" className="form-control mb-1" value={hourlyRate} onChange={(e) => setHourlyRate(e.target.value)} placeholder="leave blank to use the project or client rate" />
        <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>
          Indirect time and non-billable projects are never invoiced.
        </div>

        {skipped && (
          <div style={{ fontSize: 12, color: '#6b7a86', marginBottom: 10 }}>
            Left out: {skipped.indirect} indirect, {skipped.nonBillable} non-billable, {skipped.noHours} with no hours in the period.
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
          <button className="acc-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="acc-btn acc-btn-primary" onClick={submit} disabled={busy || !clientId}>
            {busy ? 'Building…' : 'Build draft invoice'}
          </button>
        </div>
      </div>
    </div>
  );
}
